/**
 * The access a merchant's Shopify app grants Tagada, the one list for the OAuth authorize route,
 * the scope checks, the connect wizard and the V2 front (by relative path), so this file imports
 * nothing.
 */

/** What Tagada needs to work: the app check refuses a wizard app without one of them. A write grants its read. */
export const SHOPIFY_CORE_SCOPES = [
  'write_products',
  'write_orders',
  'write_draft_orders',
  'write_customers',
  'write_discounts',
  'write_themes',
  'read_fulfillments',
] as const;

export type ShopifyCoreScope = (typeof SHOPIFY_CORE_SCOPES)[number];

/**
 * Asked for with the core, never required: a grant without them connects and reads as healthy as
 * before. write_order_edits lets Tagada edit an order already sent to Shopify (docs/adr/0001).
 */
const SHOPIFY_WANTED_SCOPES = ['write_order_edits'] as const;

/**
 * The line merchants paste into the app's "Scopes" field: the core, the wanted writes and every read
 * the Dev Dashboard grants in one approval, as proven on a merchant-org and a partner-org app on
 * 2026-10-27, less the legacy read_analytics (read_reports covers ShopifyQL) and the reads a write
 * above grants. A read missing from a grant never blocks and never makes a health state. Shopify's
 * form refuses read_all_orders, read_users and read_apps: none of them may enter it.
 */
const SHOPIFY_COPY_SCOPES: readonly string[] = [
  ...SHOPIFY_CORE_SCOPES,
  ...SHOPIFY_WANTED_SCOPES,
  'read_inventory',
  'read_locations',
  'read_markets',
  'read_markets_home',
  'read_shipping',
  'read_locales',
  'read_translations',
  'read_purchase_options',
  'read_merchant_managed_fulfillment_orders',
  'read_assigned_fulfillment_orders',
  'read_third_party_fulfillment_orders',
  'read_custom_fulfillment_services',
  'read_fulfillment_constraint_rules',
  'read_returns',
  'read_reports',
  'read_analytics_annotations',
  'read_audit_events',
  'read_shopify_payments_accounts',
  'read_shopify_payments_payouts',
  'read_shopify_payments_disputes',
  'read_shopify_payments_bank_accounts',
  'read_payment_terms',
  'read_payment_customizations',
  'read_payment_notifications',
  'read_cash_tracking',
  'read_marketing_events',
  'read_marketing_integrated_campaigns',
  'read_pixels',
  'read_custom_pixels',
  'read_customer_events',
  'read_script_tags',
  'read_publications',
  'read_channels',
  'read_product_listings',
  'read_product_feeds',
  'read_resource_feedbacks',
  'read_checkout_branding_settings',
  'read_checkout_and_accounts_configurations',
  'read_checkouts',
  'read_cart_transforms',
  'read_all_cart_transforms',
  'read_validations',
  'read_delivery_customizations',
  'read_delivery_option_generators',
  'read_price_rules',
  'read_legal_policies',
  'read_privacy_settings',
  'read_gift_cards',
  'read_gift_card_transactions',
  'read_store_credit_accounts',
  'read_store_credit_account_transactions',
  'read_companies',
  'read_customer_merge',
  'read_customer_data_erasure',
  'read_metaobjects',
  'read_metaobject_definitions',
  'read_files',
  'read_content',
  'read_online_store_navigation',
  'read_online_store_pages',
  'read_inventory_shipments',
  'read_inventory_shipments_received_items',
  'read_inventory_transfers',
  'read_inventory_purchase_orders',
  'read_packing_slip_templates',
  'read_discovery',
  'read_rollouts',
  'read_app_proxy',
  'read_checkout_kit_enhanced_buyer_events',
];

export const SHOPIFY_SCOPE_LINE: string = SHOPIFY_COPY_SCOPES.join(',');

/** The core and the wanted writes, for the authorize URL: the app version decides the grant, this only covers the legacy install flow. */
export const SHOPIFY_AUTHORIZE_SCOPE_LINE: string = [...SHOPIFY_CORE_SCOPES, ...SHOPIFY_WANTED_SCOPES].join(',');

/** Shopify lists a write's read next to it; an older answer can hold only the write. */
export function isShopifyScopeGranted(scope: string, granted: readonly string[]): boolean {
  return granted.includes(scope) || (scope.startsWith('read_') && granted.includes(`write_${scope.slice('read_'.length)}`));
}

/** The core scopes a grant lacks: the only ones that block. */
export function missingShopifyScopes(granted: readonly string[]): ShopifyCoreScope[] {
  return SHOPIFY_CORE_SCOPES.filter((scope) => !isShopifyScopeGranted(scope, granted));
}
